import { Controller, useForm } from 'react-hook-form';

import { AppRoutes, ERROR_MAX_LENGTH, ERROR_REQUIRED_FIELD } from '@/constants';
import { navigate } from '@/providers';
import {
  createNameofProxy,
  getDirtyFields,
  handleServerValidationErrors,
  showAlertSuccess,
} from '@/utils';

import {
  Button,
  Input,
  Loading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/common';

import {
  FACE_ID_PROFILE_FULL_NAME_MAX_LENGTH,
  FACE_ID_PROFILE_FULL_NAME_MIN_LENGTH,
  FACE_ID_PROFILE_REMARKS_MAX_LENGTH,
  PROFILE_TYPE_OPTIONS,
} from '@/features/face-id-profiles/constants';
import { ProfileType } from '@/features/face-id-profiles/enums';
import { IFaceIdProfileUpdate } from '@/features/face-id-profiles/interfaces';

import EmployeeProfileSection from './EmployeeProfileSection';
import GuestProfileSection from './GuestProfileSection';
import { useFaceIdProfileMutation, useFaceIdProfileQuery } from '@/features/face-id-profiles/hooks';
import { useParams } from 'react-router';
import { useEffect } from 'react';
import { UseQueryResult } from '@tanstack/react-query';

const formFields = createNameofProxy<IFaceIdProfileUpdate>();

const FaceIdProfileUpdate = () => {
  const { id } = useParams();
  const { data, isFetching, error } = useFaceIdProfileQuery(id || '') as UseQueryResult<
    IFaceIdProfileUpdate,
    Error
  >;
  const { updateMutation } = useFaceIdProfileMutation(id || '');

  const {
    handleSubmit,
    control,
    formState: { errors, dirtyFields },
    setValue,
    setError,
    trigger,
  } = useForm<IFaceIdProfileUpdate>({
    values: data,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  useEffect(() => {
    if (error) {
      navigate(AppRoutes.NOT_FOUND, { replace: true });
    }
  }, [error]);

  const onSubmit = async (data: IFaceIdProfileUpdate) => {
    const changedData = getDirtyFields(data, dirtyFields);
    changedData.profileId = id;
    changedData.profileType = data.profileType;
    await updateMutation.mutateAsync(changedData as IFaceIdProfileUpdate, {
      onSuccess: () => {
        navigate(AppRoutes.FACE_ID_PROFILES);
        showAlertSuccess(`Face Profile ID updated successfully!`);
      },
      onError: (error) => {
        handleServerValidationErrors(error, setError);
      },
    });
  };
  return (
    <div className="flex flex-col gap-base mt-xl mb-24 text-brand-primary-darkest">
      {/* Header with Title */}
      <h1 className="text-h1 font-bold">Edit Profile</h1>

      <div className="rounded-softer border border-neutral-n4 bg-neutral-white">
        <div className="text-sm flex flex-col gap-y-base-plus p-m">
          <Controller
            name={formFields.fullName}
            control={control}
            rules={{
              required: ERROR_REQUIRED_FIELD,
              minLength: {
                value: FACE_ID_PROFILE_FULL_NAME_MIN_LENGTH,
                message: ERROR_REQUIRED_FIELD,
              },
              maxLength: {
                value: FACE_ID_PROFILE_FULL_NAME_MAX_LENGTH,
                message: ERROR_MAX_LENGTH(FACE_ID_PROFILE_FULL_NAME_MAX_LENGTH),
              },
            }}
            render={({ field }) => (
              <Input
                onChange={field.onChange}
                value={field.value}
                label="Full Name"
                placeholder="Full name"
                required
                maxLength={FACE_ID_PROFILE_FULL_NAME_MAX_LENGTH}
                error={errors[formFields.fullName]?.message}
              />
            )}
          />
          <Select value={data?.profileType?.toString()} label="Profile Type" required>
            <SelectTrigger disabled className="w-full">
              <SelectValue placeholder="Select Profile Type" />
            </SelectTrigger>
            <SelectContent>
              {PROFILE_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {data?.profileType === ProfileType.Employee && (
            <EmployeeProfileSection
              defaultValues={{
                photoUrl: data.photoUrl,
                photoName: data.photoName,
                employeeId: data.employeeId,
              }}
              formFields={formFields}
              control={control}
              errors={errors}
            />
          )}
          {data?.profileType === ProfileType.Guest && (
            <GuestProfileSection
              defaultValues={{
                guestValidFrom: data.guestValidFrom,
                guestValidTo: data.guestValidTo,
              }}
              formFields={formFields}
              control={control}
              errors={errors}
              setValue={setValue}
              trigger={trigger}
            />
          )}
          {Number.isInteger(data?.profileType) && (
            <Controller
              name={formFields.remarks}
              control={control}
              rules={{
                maxLength: {
                  value: FACE_ID_PROFILE_REMARKS_MAX_LENGTH,
                  message: ERROR_MAX_LENGTH(FACE_ID_PROFILE_REMARKS_MAX_LENGTH),
                },
              }}
              render={({ field }) => (
                <Textarea
                  label="Remark"
                  placeholder="Enter remarks"
                  className="[&_textarea]:min-h-28"
                  value={field.value}
                  onChange={field.onChange}
                  maxLength={FACE_ID_PROFILE_REMARKS_MAX_LENGTH}
                  error={errors[formFields.remarks]?.message}
                />
              )}
            />
          )}
        </div>
      </div>
      {/* back and submit button */}
      <div className="flex justify-end gap-s-plus">
        <Button
          className="w-[104px]"
          size="lg"
          variant="cancel"
          borderRadius="soft"
          onClick={() => navigate(AppRoutes.FACE_ID_PROFILES)}
        >
          Back
        </Button>
        <Button
          className="w-[104px]"
          size="lg"
          variant="primary"
          borderRadius="soft"
          onClick={handleSubmit(onSubmit)}
          disabled={updateMutation.isPending}
        >
          Submit
        </Button>
      </div>
      {isFetching && <Loading fullScreen />}
    </div>
  );
};

export default FaceIdProfileUpdate;
